import { useCallback, useEffect, useState } from 'react';

export const CONSENT_STORAGE_KEY = 'unipostudio.consent';
/**
 * Bumped when what a choice covers changes, so everyone is asked again.
 * 2: analytics links signed-in page views to the account (was "anonymous").
 */
export const CONSENT_VERSION = 2;

export interface ConsentChoice {
  /** Always true: the session cookie and the language preference. */
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  /** ISO timestamp of the choice. */
  at: string;
  /** The CONSENT_VERSION the choice was made under. */
  v: number;
}

export interface UseConsentResult {
  /** The stored choice, or null when none has been made on this device. */
  consent: ConsentChoice | null;
  hasChoice: boolean;
  acceptAll: () => void;
  onlyNecessary: () => void;
  save: (choice: { analytics: boolean; marketing: boolean }) => void;
  /** Forgets the choice so the bar shows again (Settings → Privacy). */
  clear: () => void;
}

export function readConsent(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentChoice> | null;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.at !== 'string') return null;
    // A choice made under older wording does not cover what it covers now.
    if (parsed.v !== CONSENT_VERSION) return null;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      at: parsed.at,
      v: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

function writeConsent(choice: ConsentChoice | null): void {
  try {
    if (choice) localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
    else localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // Storage unavailable: the choice holds for this page only.
  }
}

/** Every mounted hook hears about a change made by another one on the same page. */
const listeners = new Set<(choice: ConsentChoice | null) => void>();

function broadcast(choice: ConsentChoice | null): void {
  for (const listener of listeners) listener(choice);
}

/**
 * GDPR consent for the public pages. Necessary storage is always on; the
 * person decides about analytics and marketing. The choice lives in
 * localStorage under `unipostudio.consent` and is shared across every hook
 * instance on the page, so the bar disappears the moment a choice is made.
 */
export function useConsent(): UseConsentResult {
  const [consent, setConsent] = useState<ConsentChoice | null>(() => readConsent());

  useEffect(() => {
    listeners.add(setConsent);
    const onStorage = (e: StorageEvent): void => {
      if (e.key === CONSENT_STORAGE_KEY || e.key === null) setConsent(readConsent());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(setConsent);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const save = useCallback((choice: { analytics: boolean; marketing: boolean }): void => {
    const next: ConsentChoice = {
      necessary: true,
      analytics: choice.analytics,
      marketing: choice.marketing,
      at: new Date().toISOString(),
      v: CONSENT_VERSION,
    };
    writeConsent(next);
    broadcast(next);
  }, []);

  const acceptAll = useCallback((): void => save({ analytics: true, marketing: true }), [save]);
  const onlyNecessary = useCallback((): void => save({ analytics: false, marketing: false }), [save]);
  const clear = useCallback((): void => {
    writeConsent(null);
    broadcast(null);
  }, []);

  return { consent, hasChoice: consent !== null, acceptAll, onlyNecessary, save, clear };
}
